// IS THIS BROWSER ONE OF OURS? (TW-0064, 2026-09-07)
//
// Chris, 2026-09-06 22:5x EDT, after testing a placard from his phone: "I've clicked through to
// test on a couple of placards btw well maybe one, I'm not sure if you've tied authenticated
// sessions to beacons or not." Nothing was tied: both placard sessions read as the shops.
//
// The site and the shop app already carry this mark (apps/marketing/lib/beacon.ts,
// apps/app/lib/beacon.ts) — the SAME key and the SAME parameter, deliberately, so one tap on
// `?wt_internal=1` covers every host in the family and a person never has to remember which one
// they marked. The form did not carry it at all, which is the half that costs a shop something:
// a test waiver signed on somebody else's page counts as one of their ten free waivers and lands
// in their client list as a client. The owner-signature rule (services/waiver-api/
// consolidated_api/owner_signature.py) cannot catch it — that rule compares the signer against
// THAT shop's owner, and one of us is not that shop's owner under any name we type.
//
// Marked per BROWSER, because that is the only honest source. It survives navigation
// (localStorage) and not a profile wipe: re-mark and carry on. `?wt_internal=0` unmarks.
//
// ⚠ IT IS A HINT AND NEVER A GATE. The flag rides the submission, so a caller can set it by hand.
// All it can do is keep a waiver out of the CLIENT count, which since 2026-09-07 is also the count the
// trial pauses at (services/waiver-api/consolidated_api/common.py `locked`), so a marked waiver uses none
// of the shop's free waivers; a paused page still refuses it, the shop is still emailed, and the row is
// still saved. Nothing is unlocked by claiming to be us.
//
// AND A BROWSER A SCRIPT DRIVES (TW-0352, 2026-10-18): scripted.ts, WebDriver's own flag, the beacon's
// second mark. Until this day a walk that signed a real shop's form on Playwright read as that shop's
// client in every read, while its beacon rows said `internal` + `scripted`. It posts `internal` now,
// and `scripted` beside it, the beacon's shape, so the body as posted (which the JSON twin beside the
// PDF keeps) says a script signed it. The API stamps `internal` alone on the row (app.py submit_waiver),
// the one mark every count reads (owner_signature.not_a_client).

import { isScripted } from "./scripted";

export const INTERNAL_KEY = "wt_internal";

/** The three methods this needs, so a test can hand it a plain object. */
type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** localStorage, or null where the browser refuses it (a private window, blocked site data) —
    reading it can THROW, not just return null, so the access itself is guarded. */
function browserStore(): Store | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** Is this browser marked as ours? No storage → nobody is internal, which is the safe default:
    a real client must never be dropped from a shop's count because their browser blocked storage. */
export function isInternal(store: Store | null = browserStore()): boolean {
  try {
    return store?.getItem(INTERNAL_KEY) === "1";
  } catch {
    return false;
  }
}

/** Mark (or unmark) this browser as ours. */
export function setInternal(on: boolean, store: Store | null = browserStore()): void {
  try {
    if (!store) return;
    if (on) store.setItem(INTERNAL_KEY, "1");
    else store.removeItem(INTERNAL_KEY);
  } catch {
    /* storage blocked — nothing to remember with, and this must never break a waiver */
  }
}

/** `?wt_internal=1` (or `=0`) on a shop's form page marks or unmarks this browser. Any other
    value — and no parameter at all — leaves the mark exactly as it stands, so an ordinary client
    arriving from a QR code can never clear one and a stray `?wt_internal=` cannot either. */
export function readInternalFromUrl(
  search: string = typeof window === "undefined" ? "" : window.location.search,
  store: Store | null = browserStore()
): void {
  try {
    const v = new URLSearchParams(search).get(INTERNAL_KEY);
    if (v === "1" || v === "0") setInternal(v === "1", store);
  } catch {
    /* malformed URL — leave the marker as it stands */
  }
}

/** What the submission carries when this browser is ours, and nothing at all when it is not — so a
    client's POST body is byte-for-byte the one it has been since 2023: `internal` for a browser marked
    by hand, and `internal` with `scripted` beside it for one a script drives (TW-0352). */
export function internalFields(
  store: Store | null = browserStore(),
  nav: { webdriver?: boolean } | undefined = typeof navigator === "undefined" ? undefined : navigator
): { internal?: true; scripted?: true } {
  if (isScripted(nav)) return { internal: true, scripted: true };
  return isInternal(store) ? { internal: true } : {};
}
